import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import PriceUpdateModal from "@/components/PriceUpdateModal";
import { Plus, PencilSimple, Trash, Pill, ChartLine, Package as PackageIcon, ListChecks, Warning, Drop, TrendUp, CurrencyCircleDollar, ShoppingCart, Archive, ArrowCounterClockwise, UploadSimple, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { USAGE_UNITS, CATEGORIES, EMPTY, DOSE_UNITS_FOR, expiryBadge, EMPTY_BULL, EMPTY_PROTOCOL, muid } from "./constants";

export function MedStock() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [priceModal, setPriceModal] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [purchaseFor, setPurchaseFor] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [catFilter, setCatFilter] = useState("all"); // "all" | "İlaç" | "Aşı"

  const load = () => api.get(`/medicines?include_archived=${includeArchived}`).then(r => setItems(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [includeArchived]);

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...form, stock: Number(form.stock), unit_price: Number(form.unit_price), min_stock: Number(form.min_stock), expiry_date: form.expiry_date || null };
    try {
      if (editId) { await api.patch(`/medicines/${editId}`, body); toast.success("Güncellendi"); }
      else { await api.post("/medicines", body); toast.success("İlaç eklendi"); }
      setShowForm(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  const edit = (x) => {
    setForm({ name: x.name, category: x.category || "İlaç", usage_unit: x.usage_unit || "ml", stock: x.stock, unit_price: x.unit_price, min_stock: x.min_stock, expiry_date: (x.expiry_date || "").slice(0, 10), supplier: x.supplier || "", notes: x.notes || "" });
    setEditId(x.id); setShowForm(true);
  };
  const archive = async (x) => {
    try { await api.patch(`/medicines/${x.id}/archive`); toast.success(`"${x.name}" arşive kaldırıldı`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };
  const unarchive = async (x) => {
    try { await api.patch(`/medicines/${x.id}/unarchive`); toast.success(`"${x.name}" arşivden çıkarıldı`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };
  const del = async (x) => {
    if (!window.confirm(`"${x.name}" KALICI olarak silinsin mi? Bu işlem geri alınamaz. (Geçmiş tedavi/kullanım kayıtları korunur.)`)) return;
    try { await api.delete(`/medicines/${x.id}`); toast.success("Kalıcı olarak silindi"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || err?.message || "Hata"); }
  };

  const shown = catFilter === "all" ? items : items.filter(x => (x.category || "İlaç") === catFilter);
  const totalValue = shown.reduce((s, x) => s + Number(x.stock_value || 0), 0);
  const lowCount = shown.filter(x => x.is_low_stock).length;
  const expiringItems = shown.filter(x => x.expiry_status === "expired" || x.expiry_status === "expiring_soon");
  const catCounts = {
    all: items.length,
    "İlaç": items.filter(x => (x.category || "İlaç") === "İlaç").length,
    "Aşı": items.filter(x => (x.category || "İlaç") === "Aşı").length,
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat testId="med-total-value" label="Toplam İlaç Stok Değeri" value={totalValue.toLocaleString("tr-TR")} unit="₺" tone="accent" />
        <Stat testId="med-item-count" label="İlaç Çeşidi" value={items.length} unit="çeşit" />
        <Stat testId="med-low-count" label="Kritik Stok" value={lowCount} unit="ilaç" tone={lowCount > 0 ? "warn" : "default"} />
        <Stat testId="med-expiring-count" label="SKT Yaklaşan/Geçen" value={expiringItems.length} unit="ilaç" tone={expiringItems.length > 0 ? "warn" : "default"} />
      </div>

      <div className="flex items-center gap-1.5 mb-4" data-testid="med-cat-filter">
        {[["all", "Tümü"], ["İlaç", "İlaç"], ["Aşı", "Aşı"]].map(([v, l]) => (
          <button key={v} type="button"
            data-testid={`med-cat-${v === "all" ? "all" : (v === "Aşı" ? "asi" : "ilac")}`}
            onClick={() => setCatFilter(v)}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors ${catFilter === v ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#2D4A36]"}`}>
            {l} <span className="opacity-70">({catCounts[v]})</span>
          </button>
        ))}
      </div>

      {expiringItems.length > 0 && (
        <div data-testid="med-expiry-alert" className="mb-6 rounded-xl border border-[#f2d4cb] bg-[#FBECEA] p-4">
          <div className="flex items-center gap-2 text-[#C45B45] font-semibold mb-2">
            <Warning size={18} weight="fill" /> Son Kullanma Tarihi Uyarısı ({expiringItems.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {expiringItems.map(x => {
              const b = expiryBadge(x.expiry_status, x.days_until_expiry);
              return (
                <span key={x.id} data-testid={`med-expiry-chip-${x.name.replace(/\s/g, '-')}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${b.cls}`}>
                  {x.name} · {b.txt}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <button data-testid="med-toggle-archived" onClick={() => setIncludeArchived(v => !v)}
                className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#E5E2DC] bg-white hover:bg-[#F5F2EC] text-[#5e675e]">
          <Archive size={15} weight="bold" /> {includeArchived ? "Aktifleri Göster" : "Arşivi Göster"}
        </button>
        <div className="flex gap-2">
          <Button variant="secondary" data-testid="med-import-btn" onClick={() => setShowImport(true)}>
            <UploadSimple size={16} className="inline mr-1" /> Excel'den İçe Aktar
          </Button>
          <Button data-testid="add-med-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }}>
            <Plus size={16} className="inline mr-1" /> Yeni İlaç
          </Button>
        </div>
      </div>

      {showForm && (
        <Card testId="med-form" className="mb-6">
          <h3 className="heading text-lg mb-4">{editId ? "İlaç Düzenle" : "Yeni İlaç Ekle"}</h3>
          <form onSubmit={submit} className="grid md:grid-cols-3 gap-4">
            <Field label="Ad *"><Input data-testid="med-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Örn: Penbiotik, Vitamin ADE" /></Field>
            <Field label="Kategori">
              <Select data-testid="med-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Kullanım Birimi *">
              <Select data-testid="med-usage-unit" value={form.usage_unit} onChange={e => setForm({ ...form, usage_unit: e.target.value })}>
                {USAGE_UNITS.map(u => <option key={u}>{u}</option>)}
              </Select>
            </Field>
            <Field label={`Stok Miktarı (${form.usage_unit}) *`}><Input data-testid="med-stock" type="number" step="0.01" required value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} /></Field>
            <Field label={`Birim Fiyat (₺ / ${form.usage_unit}) *`}><Input data-testid="med-price" type="number" step="0.01" required value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} /></Field>
            <Field label={`Min. Stok (${form.usage_unit})`}><Input data-testid="med-min-stock" type="number" step="0.01" value={form.min_stock} onChange={e => setForm({ ...form, min_stock: e.target.value })} /></Field>
            <Field label="Son Kullanma Tarihi (SKT)"><Input data-testid="med-expiry" type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} /></Field>
            <Field label="Tedarikçi"><Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} /></Field>
            <div className="md:col-span-2"><Field label="Notlar"><Textarea rows={1} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field></div>
            <div className="md:col-span-3 flex gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={() => { setShowForm(false); setForm(EMPTY); setEditId(null); }}>İptal</Button>
              <Button type="submit" data-testid="med-submit">{editId ? "Kaydet" : "Ekle"}</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="!p-0">
        <div className="overflow-x-auto">
        <table className="data-table" data-testid="med-table">
          <thead>
            <tr>
              <th>İlaç</th>
              <th>Kategori</th>
              <th>Birim</th>
              <th className="text-right">Stok</th>
              <th className="text-right">Min</th>
              <th>SKT</th>
              <th className="text-right">Birim Fiyat</th>
              <th className="text-right">Stok Değeri</th>
              <th className="text-right whitespace-nowrap">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(x => (
              <tr key={x.id} data-testid={`med-row-${x.name.replace(/\s/g, '-')}`} className={x.archived ? "opacity-55" : ""}>
                <td className="font-semibold">
                  <Pill size={14} weight="duotone" className="inline mr-1.5 text-[#417251]" />{x.name}
                  {x.archived && <span data-testid={`med-archived-badge-${x.id}`} className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#EEECE5] text-[#6b746c] px-1.5 py-0.5 rounded"><Archive size={10} weight="bold" /> Arşivde</span>}
                </td>
                <td>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${(x.category === "Aşı") ? "bg-[#E4EEE6] text-[#2D4A36] border-[#c2d6c8]" : "bg-[#FEF6E0] text-[#8B6914] border-[#EED7A6]"}`}>
                    {x.category || "İlaç"}
                  </span>
                </td>
                <td>{x.usage_unit}</td>
                <td className={`text-right tabnum ${x.is_low_stock ? "text-[#C45B45] font-bold" : ""}`}>{Number(x.stock).toLocaleString("tr-TR")}</td>
                <td className="text-right tabnum text-sm">{Number(x.min_stock).toLocaleString("tr-TR")}</td>
                <td className="text-sm whitespace-nowrap">
                  {x.expiry_date ? (() => {
                    const b = expiryBadge(x.expiry_status, x.days_until_expiry);
                    return b
                      ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${b.cls}`}>{(x.expiry_date || "").slice(0, 10)}</span>
                      : <span className="text-[#5e675e]">{(x.expiry_date || "").slice(0, 10)}</span>;
                  })() : <span className="text-[#b8beb8]">—</span>}
                </td>
                <td className="text-right tabnum">{Number(x.unit_price).toLocaleString("tr-TR")} ₺</td>
                <td className="text-right tabnum font-semibold text-[#2D4A36]">{Number(x.stock_value).toLocaleString("tr-TR")} ₺</td>
                <td className="text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    {!x.archived && (
                      <button onClick={() => setPurchaseFor(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#E4EEE6] text-[#2D4A36] hover:bg-[#d4e3d7] border border-[#c2d6c8]"
                              title="Hızlı Alım (miktar + fiyat girişi)"
                              data-testid={`med-purchase-btn-${x.name.replace(/\s/g, '-')}`}>
                        <Plus size={14} weight="bold" />
                        <span className="hidden lg:inline">Alım</span>
                      </button>
                    )}
                    {!x.archived && (
                      <button onClick={() => setPriceModal(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#FEF6E0] text-[#8B6914] hover:bg-[#f7e6b8] border border-[#EED7A6]"
                              title="Fiyat Güncelle"
                              data-testid={`med-price-btn-${x.name.replace(/\s/g, '-')}`}>
                        <CurrencyCircleDollar size={14} weight="bold" />
                        <span className="hidden lg:inline">Fiyat</span>
                      </button>
                    )}
                    {!x.archived && (
                      <button onClick={() => edit(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#F5F2EC] text-[#1A1D1A] hover:bg-[#eae5db] border border-[#E5E2DC]"
                              title="Düzenle"
                              data-testid={`edit-med-${x.name.replace(/\s/g, '-')}`}>
                        <PencilSimple size={14} weight="bold" />
                        <span className="hidden lg:inline">Düzenle</span>
                      </button>
                    )}
                    {x.archived ? (
                      <button onClick={() => unarchive(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#E4EEE6] text-[#2D4A36] hover:bg-[#d4e3d7] border border-[#c2d6c8]"
                              title="Arşivden Geri Getir"
                              data-testid={`med-unarchive-${x.name.replace(/\s/g, '-')}`}>
                        <ArrowCounterClockwise size={14} weight="bold" />
                        <span className="hidden lg:inline">Geri Getir</span>
                      </button>
                    ) : (
                      <button onClick={() => archive(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#F5F2EC] text-[#6b746c] hover:bg-[#eae5db] border border-[#E5E2DC]"
                              title="Arşive Kaldır (geçmiş kayıtlar korunur)"
                              data-testid={`med-archive-${x.name.replace(/\s/g, '-')}`}>
                        <Archive size={14} weight="bold" />
                        <span className="hidden lg:inline">Arşivle</span>
                      </button>
                    )}
                    {isOwner && (
                      <button onClick={() => del(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#FBECEA] text-[#C45B45] hover:bg-[#f5d5ce] border border-[#f2d4cb]"
                              title="Kalıcı Sil (yalnızca Admin — geri alınamaz)"
                              data-testid={`del-med-${x.name.replace(/\s/g, '-')}`}>
                        <Trash size={14} weight="bold" />
                        <span className="hidden lg:inline">Kalıcı Sil</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={9} className="text-center py-10 text-[#8a938c]">{includeArchived ? "Arşivde ilaç yok." : (catFilter === "all" ? "Henüz ilaç yok." : `Bu kategoride (${catFilter}) kayıt yok.`)}</td></tr>}
          </tbody>
        </table>
        </div>
      </Card>

      {priceModal && (
        <PriceUpdateModal
          item={{ id: priceModal.id, name: priceModal.name, unit_price: priceModal.unit_price }}
          endpoint={`/medicines/${priceModal.id}/price`}
          historyEndpoint={`/medicines/${priceModal.id}/price-history`}
          trendEndpoint={`/medicines/${priceModal.id}/price-trend?months=12`}
          unitLabel={priceModal.usage_unit || "birim"}
          title="İlaç Fiyat Güncelleme"
          subtitle={priceModal.name}
          impactNote="Geçmiş ilaç kullanım ve sayım finans kayıtları etkilenmez — kullanıldıkları anki fiyatta donmuş kalır. Sadece bundan sonraki kullanımlar yeni fiyattan hesaplanır."
          onDone={() => { setPriceModal(null); load(); }}
          onClose={() => setPriceModal(null)}
        />
      )}

      {purchaseFor && (
        <MedPurchaseModal item={purchaseFor} onClose={() => setPurchaseFor(null)} onDone={() => { setPurchaseFor(null); load(); }} />
      )}

      {showImport && (
        <MedicineExcelImport onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />
      )}
    </div>
  );
}

function MedPurchaseModal({ item, onClose, onDone }) {
  const unit = item.usage_unit || "birim";
  const [form, setForm] = useState({ quantity: "", unit_price: item.unit_price ?? 0, supplier: item.supplier || "", date: new Date().toISOString().slice(0, 10), notes: "" });
  const [busy, setBusy] = useState(false);
  const qty = Number(form.quantity || 0);
  const price = Number(form.unit_price || 0);
  const cost = qty * price;
  const priceChanged = price > 0 && Math.abs(price - Number(item.unit_price || 0)) > 1e-9;

  const submit = async (e) => {
    e.preventDefault();
    if (qty <= 0) return toast.error("Miktar 0'dan büyük olmalı");
    setBusy(true);
    try {
      const r = await api.post(`/medicines/${item.id}/purchase`, {
        quantity: qty, unit_price: price, supplier: form.supplier || null, date: form.date, notes: form.notes || null,
      });
      toast.success(`Alım kaydedildi · Yeni stok: ${Number(r.data.new_stock).toLocaleString("tr-TR")} ${unit}${cost > 0 ? ` · ${cost.toLocaleString("tr-TR")} ₺ gider` : ""}`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-md w-full" onClick={e => e.stopPropagation()} testId="med-purchase-modal">
        <div className="flex items-start justify-between mb-1">
          <h3 className="heading text-lg flex items-center gap-1.5"><ShoppingCart size={18} weight="duotone" className="text-[#2D4A36]" /> Hızlı Alım — {item.name}</h3>
          <button onClick={onClose} className="text-[#8a938c] hover:text-[#1A1D1A]" data-testid="med-purchase-close"><X size={18} /></button>
        </div>
        <div className="text-xs text-[#6b746c] mb-3">Mevcut stok: <b className="tabnum">{Number(item.stock).toLocaleString("tr-TR")} {unit}</b> · Mevcut fiyat: <b className="tabnum">{Number(item.unit_price || 0).toLocaleString("tr-TR")} ₺</b></div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field label={`Gelen Miktar (${unit}) *`}>
            <Input data-testid="med-purchase-qty" type="number" step="0.01" min="0" required autoFocus value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
          </Field>
          <Field label={`Birim Fiyat (₺/${unit})`}>
            <Input data-testid="med-purchase-price" type="number" step="0.01" min="0" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} />
          </Field>
          <Field label="Tarih"><Input data-testid="med-purchase-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Tedarikçi"><Input data-testid="med-purchase-supplier" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} placeholder="Opsiyonel" /></Field>
          <div className="col-span-2"><Field label="Notlar"><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Örn. fatura no" /></Field></div>
          <div className="col-span-2 text-sm px-3 py-2 rounded-md bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36]" data-testid="med-purchase-preview">
            Yeni stok: <b className="tabnum">{(Number(item.stock) + qty).toLocaleString("tr-TR")} {unit}</b>
            {cost > 0 && <> · Finans'a <b className="tabnum">{cost.toLocaleString("tr-TR")} ₺</b> "Mal Alımı" gideri yazılır</>}
            {priceChanged && <><br />⚠️ Birim fiyat güncellenecek ve fiyat geçmişine eklenecek (geçmiş kayıtlar etkilenmez).</>}
          </div>
          <div className="col-span-2 flex gap-2 justify-end">
            <Button type="button" variant="secondary" onClick={onClose}>Vazgeç</Button>
            <Button type="submit" data-testid="med-purchase-submit" disabled={busy}>{busy ? "Kaydediliyor..." : "Alımı Kaydet"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// ============================================================
// İLAÇ EXCEL İÇE AKTARIM MODALI (upload → önizleme → uygula)
// ============================================================

function MedicineExcelImport({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const onFileChoose = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setResult(null);
    const fd = new FormData();
    fd.append("entity_type", "medicines");
    fd.append("file", f);
    try {
      const r = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Excel okunamadı"); setFile(null); }
  };
  const apply = async () => {
    if (!file) return;
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", "medicines");
      fd.append("file", file);
      const r = await api.post("/import/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`✓ ${r.data.message}`);
    } catch (e) { toast.error(e.response?.data?.detail || "İçe aktarım hatası"); }
    finally { setApplying(false); }
  };
  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} testId="med-import-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="heading text-lg flex items-center gap-1.5"><UploadSimple size={18} weight="duotone" className="text-[#8B6914]" /> İlaçları Excel'den İçe Aktar</h3>
            <div className="text-xs text-[#5e675e] mt-0.5">Sütunlar otomatik tanınır (Türkçe/İngilizce): Ad · Kategori · Birim · Stok · Fiyat · Min Stok · SKT · Tedarikçi</div>
          </div>
          <button onClick={onClose} className="text-[#8a938c] hover:text-[#1A1D1A]" data-testid="med-import-close"><X size={18} /></button>
        </div>

        {result ? (
          <div data-testid="med-import-result" className="space-y-3">
            <div className="p-4 rounded-md bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36]">
              <div className="font-semibold mb-1">✓ İçe aktarım tamamlandı</div>
              <div className="text-sm">{result.imported} ilaç eklendi, {result.skipped} satır atlandı.</div>
              {result.errors?.length > 0 && (
                <ul className="mt-2 text-xs text-[#8B3A29] list-disc pl-5">{result.errors.map((er, i) => <li key={`${i}-${er}`}>{er}</li>)}</ul>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={reset} data-testid="med-import-again">Yeni Dosya</Button>
              <Button onClick={onDone} data-testid="med-import-done">Kapat & Yenile</Button>
            </div>
          </div>
        ) : !preview ? (
          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFileChoose(f); }}
            data-testid="med-import-dropzone"
            className={`block cursor-pointer border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? "border-[#2D4A36] bg-[#E4EEE6]" : "border-[#E5E2DC] hover:border-[#B4C1B7] bg-[#FAF8F4]"}`}>
            <UploadSimple size={36} weight="duotone" className="mx-auto mb-2 text-[#5e675e]" />
            <div className="font-semibold text-[#1A1D1A] mb-1">Excel dosyasını buraya bırakın veya tıklayarak seçin</div>
            <div className="text-xs text-[#5e675e]">.xlsx · İlk satır başlık olmalı · yalnızca "Ad" zorunlu</div>
            <input type="file" accept=".xlsx" className="hidden" data-testid="med-import-file" onChange={(e) => onFileChoose(e.target.files?.[0])} />
          </label>
        ) : (
          <div data-testid="med-import-preview">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-[#5e675e]">{preview.total_rows} satır · {Object.keys(preview.mapping).length} alan eşlendi</div>
              <button onClick={reset} className="text-sm text-[#8a938c] hover:text-[#1A1D1A]" data-testid="med-import-reset"><X size={14} className="inline mr-1" /> Vazgeç</button>
            </div>
            {preview.warnings?.length > 0 && (
              <div className="mb-3 p-3 rounded bg-[#fbeee9] border border-[#f2d4cb] text-sm text-[#8B3A29]">⚠️ {preview.warnings.join(" · ")}</div>
            )}
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.entries(preview.mapping).map(([canon, idx]) => (
                <div key={canon} className="text-xs bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36] rounded px-2 py-1">
                  <span className="italic">{preview.headers[idx]}</span><ArrowRight size={10} className="inline mx-1" /><b>{canon}</b>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto border border-[#E5E2DC] rounded mb-4">
              <table className="w-full text-xs">
                <thead><tr className="bg-[#FAF8F4] border-b border-[#EEECE5]">{Object.keys(preview.mapping).map(k => <th key={k} className="p-2 text-left font-semibold">{k}</th>)}</tr></thead>
                <tbody>
                  {preview.sample.map((row, i) => (
                    <tr key={i} className={i % 2 ? "bg-[#FBFAF7]" : ""}>
                      {Object.keys(preview.mapping).map(k => <td key={k} className="p-2 border-t border-[#F5F2EC] tabnum">{row[k] === null || row[k] === undefined ? <span className="text-[#c4c9c1]">—</span> : String(row[k])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={reset}>Vazgeç</Button>
              <Button onClick={apply} disabled={applying || preview.missing_required?.length > 0} data-testid="med-import-apply">
                {applying ? "İçe aktarılıyor..." : `✓ ${preview.total_rows} İlacı İçe Aktar`}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// TEDAVİ PROTOKOLLERİ — çok-ilaçlı tedavi paketleri
// ============================================================
